/**
 * AlertsPage — Active (unresolved) alerts across all machines.
 * Click a row to navigate to the machine detail page.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAlerts } from '../hooks/useAlerts';
import { useMachines } from '../hooks/useMachines';
import { resolveAlert, sendTelegramTest } from '../api/client';

const TYPE_LABEL: Record<string, string> = {
  OFFLINE: 'Offline',
  LOW_PAPER: 'Low paper',
  LOW_TONER: 'Low toner',
  // Киоск сообщает о них сам: экран отвалился от службы / принтер говорит,
  // что все лотки пусты.
  UI_DOWN: 'Screen down',
  PAPER_OUT: 'Out of paper',
  // Покупатель поставил «плохо» на экране «Hotovo» — по алерту на отзыв.
  BAD_FEEDBACK: 'Bad review',
};

const SEVERITY_COLOR: Record<string, { bg: string; fg: string }> = {
  CRITICAL: { bg: 'rgba(239,68,68,0.18)', fg: '#b91c1c' },
  WARNING: { bg: 'rgba(234,179,8,0.18)', fg: '#a16207' },
};

/** canAct=false — роль operator: без Resolve и без тестовой отправки. */
export function AlertsPage({ canAct = true }: { canAct?: boolean }) {
  const { alerts, loading, error, refresh } = useAlerts();
  const { machines } = useMachines();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [tgState, setTgState] = useState<'idle' | 'sending' | 'sent'>('idle');

  async function handleTelegramTest() {
    setTgState('sending');
    setActionError(null);
    try {
      await sendTelegramTest();
      setTgState('sent');
      setTimeout(() => setTgState('idle'), 4000);
    } catch (e) {
      setTgState('idle');
      setActionError(e instanceof Error ? e.message : 'Telegram test failed');
    }
  }

  const machineMap = new Map(machines.map((m) => [m.machineId, m]));

  async function handleResolve(id: string) {
    setBusyId(id);
    setActionError(null);
    try {
      await resolveAlert(id);
      refresh();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Could not resolve alert');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6" style={{ flexWrap: 'wrap', gap: 8 }}>
        <h2>Active Alerts</h2>
        <div className="flex gap-2">
          {canAct && <button
            className="btn btn-secondary btn-sm"
            onClick={() => void handleTelegramTest()}
            disabled={tgState === 'sending'}
            title="Send a test message to the Telegram alerts group"
          >
            {tgState === 'sending' ? 'Sending…' : tgState === 'sent' ? 'Sent to Telegram ✓' : 'Test Telegram'}
          </button>}
          <button className="btn btn-secondary btn-sm" onClick={refresh} disabled={loading}>
            {loading ? '...' : 'Refresh'}
          </button>
        </div>
      </div>

      {(error || actionError) && (
        <div className="card mb-6">
          <div className="card-body">
            <div style={{ color: 'var(--color-offline)', fontWeight: 500 }}>Error: {error ?? actionError}</div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-body">
          {loading && alerts.length === 0 ? (
            <div className="empty-state"><h3>Loading...</h3></div>
          ) : alerts.length === 0 ? (
            <div className="empty-state">
              <p className="text-muted">No active alerts. Everything is good.</p>
            </div>
          ) : (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Since</th>
                    <th>Severity</th>
                    <th>Type</th>
                    <th>Machine</th>
                    <th>Message</th>
                    {canAct && <th></th>}
                  </tr>
                </thead>
                <tbody>
                  {alerts.map((a) => {
                    const sev = SEVERITY_COLOR[a.severity] ?? { bg: 'rgba(100,116,139,0.15)', fg: '#475569' };
                    const machine = machineMap.get(a.machineId);
                    const machineLabel = machine ? `${machine.name} (#${a.machineId})` : `#${a.machineId}`;
                    return (
                      <tr key={a.id}>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {new Date(a.createdAt).toLocaleString()}
                        </td>
                        <td>
                          <span
                            style={{
                              fontSize: '0.74rem',
                              fontWeight: 600,
                              padding: '2px 8px',
                              borderRadius: 4,
                              background: sev.bg,
                              color: sev.fg,
                              letterSpacing: '0.02em',
                            }}
                          >
                            {a.severity}
                          </span>
                        </td>
                        <td>{TYPE_LABEL[a.alertType] ?? a.alertType}</td>
                        <td>
                          <Link to={`/machines/${a.machineId}`}>{machineLabel}</Link>
                        </td>
                        <td>{a.message}</td>
                        {canAct && <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                          <button
                            className="btn btn-secondary btn-sm"
                            style={{ fontSize: '0.78rem' }}
                            disabled={busyId === a.id}
                            title="Close this alert by hand (e.g. toner replaced, printer fixed)"
                            onClick={() => void handleResolve(a.id)}
                          >
                            {busyId === a.id ? 'Resolving…' : 'Resolve'}
                          </button>
                        </td>}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
